import { attr, belongsTo, hasMany } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import moment from 'moment-timezone';
import { formatISODate } from 'volta/utils/date-utils';
import { dateCriticality } from 'volta/utils/style-utils';
import BaseModel from './base-model';

import type { IResourceName } from './base-model';
import type {
  IAggTasksView,
  IAggTasksViewMap,
  ITask,
  TAssignTo,
  TTaskResolveIf,
  TTaskType,
  UTask
} from './types/tasks';
import type UserProfile from './user-profile';
import type { TEntityType } from './types/entity-types';

export type TaskEntityType = Extract<
  TEntityType,
  'skus' | 'planningOrders' | 'simulations' | 'simulationItemSettings' | 'forecastProjects'
>;

export const TASK_TYPES: TTaskType[] = [
  'COMMENT',
  'SYNCHRONISATION',
  'SCHEDULING',
  'PLANNING_TASK',
  'EXECUTION_TASK',
  'PO_TASK',
  'SKU_TASK',
  'SIMULATION_TASK',
  'SIMULATION_ITEM_TASK',
  'FORECAST_TASK',
  'FORECAST_PROJECT_TASK'
];

export const SKU_TASK_TYPES: TTaskType[] = [
  'SKU_TASK',
  'EXECUTION_TASK',
  'PLANNING_TASK',
  'FORECAST_TASK'
];

export enum TaskCommands {
  CreateTask = 'CreateTask',
  UpdateTask = 'UpdateTask',
  DeleteTask = 'DeleteTask',
  ResolveTask = 'ResolveTask',
  ReopenTask = 'ReopenTask'
}

export enum TaskEvents {
  TaskCreated = 'TaskCreated',
  TaskUpdated = 'TaskUpdated',
  TaskDeleted = 'TaskDeleted',
  TaskResolved = 'TaskResolved',
  TaskReopened = 'TaskReopened'
}

export const TaskListChangeEvents = [
  TaskEvents.TaskUpdated,
  TaskEvents.TaskResolved,
  TaskEvents.TaskReopened,
  TaskEvents.TaskCreated,
  TaskEvents.TaskDeleted
];

export const TaskResourceName: IResourceName = {
  singular: 'task',
  plural: 'tasks'
};

export const SkuTaskResourceName: IResourceName = {
  singular: 'skuTask',
  plural: 'skuTasks'
};

export const PoTaskResourceName: IResourceName = {
  singular: 'poTask',
  plural: 'poTasks'
};

export const SimulationTaskResourceName: IResourceName = {
  singular: 'simulationTask',
  plural: 'simulationTasks'
};

export const SimulationItemSettingTaskResourceName: IResourceName = {
  singular: 'simulationItemSettingTask',
  plural: 'simulationItemSettingTasks'
};

export const ForecastProjectTaskResourceName: IResourceName = {
  singular: 'forecastProjectTask',
  plural: 'forecastProjectTasks'
};

export const TaskResourceNameForEntityType: Record<TaskEntityType, IResourceName> = {
  planningOrders: PoTaskResourceName,
  skus: SkuTaskResourceName,
  simulations: SimulationTaskResourceName,
  simulationItemSettings: SimulationItemSettingTaskResourceName,
  forecastProjects: ForecastProjectTaskResourceName
};

export function taskToCommand(task: UTask): Partial<ITask> {
  const cmd: Partial<ITask> = {
    id: task.id,
    taskType: task.taskType,
    parentId: task.parentId,
    dueDate: task.dueDate ? formatISODate(task.dueDate) : task.dueDate,
    comment: task.comment,
    payload: task.payload,
    assigneeIds: task.assigneeIds,
    criticality: task.criticality,
    priority: task.priority,
    resolveIf: task.resolveIf,
    linkedEntityId: task.linkedEntityId,
    resolvedAt:
      task.resolvedAt === null
        ? null
        : task.resolvedAt
          ? moment.utc(task.resolvedAt).toISOString()
          : undefined
  };

  if (task.id) {
    cmd.taskId = task.id;
  } else {
    cmd.createdById = task.createdBy?.id;
  }
  return cmd;
}

const DefaultAggTasksView: IAggTasksView = {
  total: 0,
  due: 0,
  maxCriticality: 0,
  minDueDate: undefined
};

export const LabelsForAssignTo: Record<TAssignTo, { title: string; empty: string }> = {
  assignments: { title: 'tasksPage.assignTo.assignments', empty: 'tasksPage.noAssignments' },
  assignings: { title: 'tasksPage.assignTo.assignings', empty: 'tasksPage.noAssignedTasks' },
  unassigned: { title: 'tasksPage.assignTo.unassigned', empty: 'tasksPage.noUnassignedTasks' }
};

export function tasksToAgg(tasks: Task[]): IAggTasksViewMap {
  const grouped: IAggTasksViewMap = {};
  tasks.forEach((t) => {
    if (t) {
      const group = grouped[t.taskType] ?? DefaultAggTasksView;
      group.total++;
      if (t.dueDate) {
        group.due++;
      }
      if (t.criticality && (group.maxCriticality ?? -1 < t.criticality)) {
        group.maxCriticality = t.criticality;
      }
      if (t.dueDate && t.dueDate < (group.minDueDate ?? '9999-01-01')) {
        group.minDueDate = t.dueDate;
      }
    }
  });

  return grouped;
}

export function indexedEntities(tasks: UTask[]) {
  const ix: Record<string, Record<string, unknown> & { linkedEntityType: TEntityType }> = {};
  tasks.forEach((c) => {
    ix[c.linkedEntityId] = { ...c.linkedEntityPayload, linkedEntityType: c.linkedEntityType };
  });

  return ix;
}

export default class Task<T = Record<string, any>, E = Record<string, any>>
  extends BaseModel
  implements ITask<T, E>
{
  /**
   * Attributes
   */
  @attr('string') linkedEntityId!: string;
  @attr('string') linkedEntityType!: TEntityType;
  @attr() linkedEntityPayload!: E;
  @attr('string') taskType!: TTaskType;
  @attr('string') parentId?: string;
  @attr('string') resolveIf?: TTaskResolveIf;
  @attr('array') assigneeIds?: string[];
  @attr('string') dueDate?: string | null;
  @attr('date') resolvedAt?: Date | null;
  @attr('string') comment?: string;
  @attr('string') createdById!: string;
  @attr('string') archivedById?: string;
  @attr('string') updatedById?: string;
  @attr('string') resolvedById?: string;
  @attr() payload?: T;
  @attr('string') action?: string;
  @attr('number') priority?: number;
  @attr('number') criticality?: number;

  @hasMany('user-profile', { async: false, inverse: null }) assignees!: UserProfile[];
  @belongsTo('user-profile', { async: false, inverse: null }) createdBy?: UserProfile;
  @belongsTo('user-profile', { async: false, inverse: null }) updatedBy?: UserProfile;
  @belongsTo('user-profile', { async: false, inverse: null }) archivedBy?: UserProfile;
  @belongsTo('user-profile', { async: false, inverse: null }) resolvedBy?: UserProfile;

  /**
   * Tracked properties
   */
  @tracked showCommentAuthor: boolean = false;
  @tracked isFirstComment: boolean = false;

  /**
   * Getters
   */

  get isResolved() {
    return !!this.resolvedAt;
  }

  get dueDateAsDate() {
    return this.dueDate ? moment.utc(this.dueDate).toDate() : undefined;
  }

  get newWithoutComment() {
    return !this.id && !this.comment;
  }

  get todoCriticality() {
    return dateCriticality(this.dueDate, !this.resolvedAt);
  }

  [Type] = 'task' as const;
}
